// TODO: this store should be migrated to DesignerStore & EditorStore and only reducer should exist here

//  ProductItemStore
//  - currentItemMeta - for inventory type, quantity, options & variants data for default item
//  - savedCurrentItemMeta - for simpler work with discard changes + for easier validation on cancelling have something changed or not
//  - currentInlineItemMeta - for inventory type, quantity, options & variants data for inline item
//  Expected structure for this fields:
// { defaultSku: -  source of truth for inventory type, quantity + other sku based things
//  variants: - data from the variants related to current product (inventory type, quantity + sku based things)
// }
// Check documentation for commerce specific fields: https://github.com/webflow/webflow/blob/dev/docs/ecommerce/commerce-specific-fields.md

import {Map, List} from 'immutable';
import {identity, take, drop, isEqual, times, remove} from 'lodash';
import {reportBug} from '@packages/utilities/reporter';
import {
  getId,
  makeMintId,
  isMint,
} from '@packages/systems/core/utils/RecordUtils';
import {
  isImmutable,
  setIn,
  updateIn,
  getIn,
} from '@packages/utilities/imm4-backports';
import {pipe} from '@packages/utilities/fp/utils';
import {ActionType} from '@packages/utilities/create-action';
import {
  DYN_ITEM_SELECTED,
  PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED,
  PRODUCT_ITEM_META_MODIFIED,
  DYN_ITEM_SAVED,
  DYN_ITEM_MINTED,
  DYN_ITEM_MODIFIED,
  DYN_INLINE_ITEM_MINTED,
  PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED,
  PRODUCT_OPTION_SET_OPTION_ADDED,
  PRODUCT_OPTION_SET_REMOVED,
  PRODUCT_OPTION_SET_OPTION_REMOVED,
  DYN_VARIANT_MODIFIED,
  DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED,
  DEFAULT_VARIANT_UPDATED,
  ITEM_DUPLICATED,
  COMMERCE_INCLUDE_DOWNLOADS_TOGGLED,
  COMMERCE_CORRUPTED_PRODUCT_DETECTED,
  COMMERCE_CORRUPTED_VARIANTS_FIXED,
  COMMERCE_PRODUCT_TYPE_CHANGED,
  RAW_ITEMS_RECEIVED,
} from '@packages/systems/designer/actions';

import {
  isProductsCollection,
  generateVariantsByOptionCombinationHash,
  assignDefaultOptionToExistingVariants,
  variantsFromNewOption,
  getOptionCombinationHash,
  optionCombinationsFromOptionSets,
  fieldsFromSkuSlugs,
  variantsFromRemovedOption,
  removeDefaultOptionFromExistingVariants,
  getVariantName,
  getVariantSlug,
  dependentProductFields,
  isRequiredShippingBeingToggledOff,
  isTrackInventoryBeingToggledOff,
  resetFieldValue,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  getInitialVariant,
  defaultSubscriptionPlan,
} from '@packages/shared/utils/SharedCommerceUtils';
import type {
  Inventory,
  Variant,
  ProductItemStateType,
  ItemMetaType,
  OptionCombinationHash,
  SkuPropertiesRecordType,
} from '@packages/systems/commerce/core';
import {
  DEFAULT_SKU_SLUG,
  INVENTORY_TYPE_INFINITE,
  INVENTORY_TYPE_FINITE,
  INFINITE_INVENTORY,
  DOWNLOAD_FILES_EDITABLE_FIELDS,
  DOWNLOAD_FILES_KEY_PATH,
  BILLING_METHOD_TYPES,
} from '@packages/systems/commerce/constants';
import {
  mintDynInlineItem,
  mintDynItem,
  selectDynItem,
  collectionItemDuplicated,
  saveDynItem,
  receiveRawItems,
  dynItemModified,
} from '@packages/systems/cms/CollectionStore';
import {
  PRODUCT_INVENTORY_FIELD_META_SLUG,
  PRODUCT_QUANTITY_FIELD_META_SLUG,
} from './constants';
import {isItemMetaSet} from './selectors';
import {
  commerceCorruptedProductDetected,
  commerceCorruptedVariantsFixed,
  defaultVariantUpdated,
  dynVariantModified,
  dynVariantImageUploadProgressed,
  productOptionSetOptionAdded,
  productOptionSetOptionRemoved,
  productOptionSetRemoved,
  productOptionSetWithDefaultOptionCreated,
  commerceIncludeDownloadsToggled,
  commerceProductTypeChanged,
  productItemMetaModified,
  ProductItemInventoriesFetchSucceededAction,
  productItemInventoriesFetched,
} from './actions';
import {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {CollectionsType} from '@packages/systems/core/records/CollectionStateType';

const SUBSCRIPTION_TRIAL_PATH = ['ec-sku-subscription-plan', 'trial'];

const merge = <S extends Record<any, any>, M extends Record<any, any>>(
  state: S,
  mergeObj: M
): S & M => {
  const allEqual = Object.keys(mergeObj).every(
    (key) => state[key] === mergeObj[key]
  );
  if (allEqual) {
    return state as S & M;
  }
  return {
    ...state,
    ...mergeObj,
  };
};

const setVariantValue =
  (keyPath: Array<number | string>, value: any) =>
  (variant: Variant): Variant => {
    if (getIn(variant, keyPath) === undefined && value === null) {
      // if we're trying to go from undefined->null, it's because a value doesn't exist and the user
      // blurred the field of the value. we're going to _not_ do a state update in this case, because
      // our form validation logic will consider it a dirty change and show the save modal.
      return variant;
    }

    if (keyPath[0] === DOWNLOAD_FILES_KEY_PATH) {
      if (keyPath.length === 1) {
        // when toggling `Include download` we are updating `download-files` with a saved value or an empty array
        return setIn(variant, [DOWNLOAD_FILES_KEY_PATH], value);
      }

      const updatedVariant = updateIn(
        variant,
        [DOWNLOAD_FILES_KEY_PATH],
        // @ts-expect-error - TS7006 - Parameter 'originalArr' implicitly has an 'any' type.
        (originalArr) => {
          // @ts-expect-error - TS2554 - Expected 4 arguments, but got 3. | TS7006 - Parameter 'original' implicitly has an 'any' type.
          return updateIn([...(originalArr || [])], [0], (original) =>
            // @ts-expect-error - TS2322 - Type 'string | number | undefined' is not assignable to type 'string | number'.
            setIn(original || {}, [keyPath[1]], value)
          );
        }
      );

      // We need to reset download-files to empty array if no values
      const updatedDownloadFilesObject =
        updatedVariant[DOWNLOAD_FILES_KEY_PATH][0];
      if (
        Object.keys(updatedDownloadFilesObject).some(
          (key) =>
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly name: true; readonly url: true; }'.
            DOWNLOAD_FILES_EDITABLE_FIELDS[key] &&
            Boolean(updatedDownloadFilesObject[key])
        )
      ) {
        return updatedVariant;
      }

      return setIn(variant, [keyPath[0]], []);
    }

    // If we're switching to billing-method subscription need to reset the default ec-sku-subscription-plan field
    if (
      keyPath[0] === 'ec-sku-billing-method' &&
      value === BILLING_METHOD_TYPES.subscription &&
      !variant['ec-sku-subscription-plan']
    ) {
      return setIn(
        setIn(variant, keyPath, value),
        ['ec-sku-subscription-plan'],
        defaultSubscriptionPlan
      );
    }

    const numberIndex = keyPath.findIndex((a) => typeof a === 'number');
    return numberIndex === -1
      ? setIn(variant, keyPath, value)
      : // @ts-expect-error - TS2554 - Expected 4 arguments, but got 3. | TS7006 - Parameter 'original' implicitly has an 'any' type.
        updateIn(variant, take(keyPath, numberIndex), (original) => {
          return setIn(original || [], drop(keyPath, numberIndex), value);
        });
  };

const initialProductMeta = {
  quantity: 0,
  inventoryType: INVENTORY_TYPE_INFINITE,
  variants: Map<OptionCombinationHash, Variant>(),
  defaultSkuId: null,
} as const;

export const initialState = {
  currentItemMeta: initialProductMeta,
  savedCurrentItemMeta: initialProductMeta,
  currentInlineItemMeta: initialProductMeta,
} as const;

type Action = ActionType<
  | typeof mintDynItem
  | typeof mintDynInlineItem
  | typeof dynItemModified
  | typeof selectDynItem
  | typeof receiveRawItems
  | typeof collectionItemDuplicated
  | typeof saveDynItem
  | typeof productItemInventoriesFetched
  | typeof productItemMetaModified
  | typeof productOptionSetWithDefaultOptionCreated
  | typeof productOptionSetOptionAdded
  | typeof productOptionSetOptionRemoved
  | typeof productOptionSetRemoved
  | typeof dynVariantModified
  | typeof dynVariantImageUploadProgressed
  | typeof defaultVariantUpdated
  | typeof commerceIncludeDownloadsToggled
  | typeof commerceCorruptedVariantsFixed
  | typeof commerceCorruptedProductDetected
  | typeof commerceProductTypeChanged
>;

export type Dependencies = {
  isInline: boolean;
  currentCollection: CollectionRecordType | null;
  currentItem: ItemRecordType | null;
  allVariants: List<Variant>;
  allCollections: CollectionsType;
  defaultCurrency: string;
};

const defaultDependencies: Dependencies = {
  isInline: false,
  currentCollection: null,
  currentItem: null,
  allVariants: List<Variant>(),
  allCollections: Map<string, CollectionRecordType>(),
  defaultCurrency: '',
};

export const makeGetDependencies =
  (getDependencies?: () => Partial<Dependencies>) => (): Dependencies =>
    getDependencies
      ? {
          ...defaultDependencies,
          ...getDependencies(),
        }
      : defaultDependencies;

export default function ProductItemStore(
  state: ProductItemStateType,
  action: Action,
  getDependencies: () => Dependencies = makeGetDependencies()
): ProductItemStateType {
  switch (action.type) {
    case DYN_INLINE_ITEM_MINTED:
    case DYN_ITEM_MINTED: {
      const {
        payload: {collectionId: itemCollectionId},
      } = action;
      const {allCollections, defaultCurrency} = getDependencies();

      // The current collection in CurrentCollectionStore is only updated
      // after this action is processed - therefore to accommodate the
      // inline case, we need to manually resolve the collection.
      const collectionAfterMint = allCollections.get(itemCollectionId);
      if (isProductsCollection(collectionAfterMint)) {
        const isInlineMinted = action.type === DYN_INLINE_ITEM_MINTED;
        const {
          payload: {initialVariantMintId, item},
        } = action;
        const initialVariant = getInitialVariant({
          productMintId: String(getId(item)),
          mintId: initialVariantMintId,
          defaultCurrencyUnit: defaultCurrency,
        });
        const adjuster = (itemMeta: ItemMetaType) =>
          merge(itemMeta, {
            defaultSkuId: initialVariantMintId,
            variants: Map([
              [
                getOptionCombinationHash(initialVariant['sku-values']),
                initialVariant,
              ],
            ]),
          });
        return pipe([
          isInlineMinted
            ? discardCurrentInlineItemMeta
            : discardCurrentItemMeta,
          isInlineMinted
            ? (workingState: any) =>
                adjustCurrentInlineItemMeta(adjuster, workingState)
            : (workingState: any) =>
                adjustCurrentItemMeta(adjuster, workingState),
        ])(state);
      }
      return state;
    }

    case DYN_ITEM_MODIFIED: {
      const {
        payload: {keyPath, value},
      } = action;
      const [fieldSlug, ...restPath] = keyPath;

      if (!fieldSlug) {
        // Type check
        return state;
      }

      // check if `Requires shipping` setting is being toggled to off
      if (isRequiredShippingBeingToggledOff(keyPath, value)) {
        // if so reset all measurment fields
        state = resetMeasurementFields(state);
      }
      const {currentCollection} = getDependencies();

      if (
        isProductsCollection(currentCollection) &&
        typeof fieldSlug !== 'undefined' &&
        // Edits to these fields on the product form
        // are applied to the default variant.
        fieldsFromSkuSlugs.includes(String(fieldSlug))
      ) {
        // because Dynamo Items are stored as ImmutableJS structures
        // and this store handles VanillaJS structures,
        // we need to cast the incoming values to VanillaJS if necessary
        const castValueToJS = fieldSlug === 'price' && isImmutable(value);
        const castValueToArray =
          fieldSlug === 'more-images' &&
          value instanceof List &&
          restPath.length === 0;
        let castValue = castValueToJS
          ? value.toJS()
          : castValueToArray
            ? // @ts-expect-error - TS2339 - Property 'toArray' does not exist on type '{}'.
              value.toArray()
            : value;

        if (
          // If we are updating the item trial
          SUBSCRIPTION_TRIAL_PATH.every((elem) => keyPath.includes(elem))
        ) {
          // default value to null if it is not greater than 0
          castValue = Number(castValue) === 0 ? null : castValue;
        }

        const itemMeta = getItemMeta(state);
        const defaultVariantId = itemMeta.defaultSkuId;

        if (!defaultVariantId) {
          // Please see https://github.com/webflow/webflow/pull/39900
          // eslint-disable-next-line webflow/better-bug-reporting
          reportBug('DefaultVariantNotFound', 'item modified', {
            metadata: {
              defaultVariantId,
              itemMeta,
              keyPath,
              castValue,
            },
          });
          return state;
        }
        return modifyVariantItem(
          state,
          // augment the keyPath with default SKU id
          [defaultVariantId, ...keyPath],
          castValue
        );
      }
      return state;
    }

    case DYN_ITEM_SELECTED: {
      const {
        payload: {item},
      } = action;
      const {currentCollection, allVariants} = getDependencies();

      if (
        currentCollection &&
        isProductsCollection(currentCollection) &&
        !isMint(item)
      ) {
        const productId = item.get('_id');
        const defaultSkuId = item.get(DEFAULT_SKU_SLUG);
        const persistedVariants = allVariants
          .filter((variant) => variant.product === productId)
          .toArray();
        const persistedVariantsByOptionCombinationHash =
          generateVariantsByOptionCombinationHash(persistedVariants);

        const defaultVariant = persistedVariants.find(
          (variant) => variant._id === defaultSkuId
        );
        if (!defaultVariant) {
          // CC-240: corrupted product (orphaned default-sku) reached the
          // reducer. Upstream guards (selectItem, CorruptedVariantsModal)
          // should normally catch this and dispatch
          // COMMERCE_CORRUPTED_PRODUCT_DETECTED, which resets this store.
          // This branch is a backstop: do not throw (would crash the
          // Designer) and do not mutate variants on top of stale state.
          // The bug report still fires for ops visibility — if it shows up
          // in Datadog it likely points at the hook-driven refetch path
          // (see CC-240 follow-up ticket).
          // Please see https://github.com/webflow/webflow/pull/39900
          // eslint-disable-next-line webflow/better-bug-reporting
          reportBug('DefaultVariantNotFound', 'no default variant found', {
            metadata: {
              persistedVariants,
              defaultSkuId,
              item: item.toJS(),
            },
          });
          return state;
        }

        return {
          ...state,
          currentItemMeta: {
            ...state.currentItemMeta,
            variants: persistedVariantsByOptionCombinationHash,
            defaultSkuId,
          },
          savedCurrentItemMeta: {
            ...state.currentItemMeta,
            variants: persistedVariantsByOptionCombinationHash,
            defaultSkuId,
          },
        };
      }

      return state;
    }

    case RAW_ITEMS_RECEIVED: {
      /**
        See https://github.com/webflow/webflow/blob/cb44e633951930e4bd5f106bcfe52a32498fb248/shared/stores/CollectionStore.js#L591-L594 as to why
        we don't want to update the current state when mergeExistingItemsTogether is true.
       */
      if (action.payload.mergeExistingItemsTogether) {
        return state;
      }
      const {currentCollection, currentItem} = getDependencies();

      if (
        !currentCollection ||
        !currentItem ||
        !isProductsCollection(currentCollection) ||
        isMint(currentItem)
      ) {
        return state;
      }

      const incomingItems = action.payload.items;
      const productId = currentItem.get('_id');

      if (
        !incomingItems.find(
          (incomingItem) => incomingItem.get('_id') === productId
        )
      ) {
        /**
          We want to verify that the incoming items contains the product we're working on so we don't
          accidentally have a mismatched product and variants in this store.
         */
        return state;
      }

      const defaultSkuId = currentItem.get(DEFAULT_SKU_SLUG);
      const persistedVariants = incomingItems.reduce<Array<Variant>>(
        (acc, skuItem) => {
          if (skuItem.get('product') === productId) {
            acc.push(skuItem.toJS() as Variant);
          }
          return acc;
        },
        []
      );
      const persistedVariantsByOptionCombinationHash =
        generateVariantsByOptionCombinationHash(persistedVariants);

      const defaultVariant = persistedVariants.find(
        (variant) => variant._id === defaultSkuId
      );
      if (!defaultVariant) {
        // CC-240: corrupted product reached the reducer via a server sync
        // (multiplayer delete, autosave round-trip, etc.). This branch is
        // NOT covered by an upstream guard in this PR — there's a CC-240
        // follow-up ticket to close it (likely via a watcher component or
        // Flux engine). Backstop: do not throw, do not overwrite variants
        // with the corrupted set. The bug report still fires so this path
        // shows up in Datadog if it happens in production.
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug('DefaultVariantNotFound', 'no default variant found', {
          metadata: {
            persistedVariants,
            defaultSkuId,
            item: currentItem.toJS(),
          },
        });
        return state;
      }

      /**
        This function helps us maintain the inventory values that are asyncly loaded by
        the PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED case. Depending on the site, either this current
        case or the inventory case will resolve first, and either way we need to safely combine the inventory
        values so they don't get overwritten.
      */
      const mergePossiblyLoadedInventoryValues = (itemMeta: ItemMetaType) => {
        return persistedVariantsByOptionCombinationHash.map(
          (variant: Variant, variantHash: OptionCombinationHash) => {
            const inventory = itemMeta.variants.get(variantHash)?.inventory;
            if (!inventory) {
              return variant;
            }
            return {
              ...variant,
              inventory,
            };
          }
        );
      };

      return merge(state, {
        currentItemMeta: {
          ...state.currentItemMeta,
          variants: mergePossiblyLoadedInventoryValues(state.currentItemMeta),
          defaultSkuId,
        },
        savedCurrentItemMeta: {
          ...state.currentItemMeta,
          variants: mergePossiblyLoadedInventoryValues(
            state.savedCurrentItemMeta
          ),
          defaultSkuId,
        },
      });
    }

    case ITEM_DUPLICATED: {
      const {
        payload: {duplicatedVariants, newDefaultVariant},
      } = action;
      if (Array.isArray(duplicatedVariants) && newDefaultVariant) {
        const inventory = newDefaultVariant.inventory || INFINITE_INVENTORY;

        if (!newDefaultVariant._id) {
          try {
            // Please see https://github.com/webflow/webflow/pull/39900
            // eslint-disable-next-line webflow/better-bug-reporting
            reportBug('DefaultVariantNotFound', 'newDefaultVariant has no ID', {
              metadata: {
                newDefaultVariant:
                  'toJS' in newDefaultVariant &&
                  typeof newDefaultVariant.toJS === 'function'
                    ? newDefaultVariant.toJS()
                    : JSON.stringify(newDefaultVariant, null, 2),
              },
            });
          } catch (error: any) {
            // bad eslint
          }
        }

        return {
          ...initialState,
          currentItemMeta: {
            variants:
              generateVariantsByOptionCombinationHash(duplicatedVariants),
            quantity: inventory.quantity,
            inventoryType: inventory.inventoryType,
            defaultSkuId: newDefaultVariant._id,
          },
          // savedCurrentItemMeta and currentInlineItemMeta are intentionally
          // reset to initial state
        };
      }
      return state;
    }

    case PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED: {
      const {
        payload: {response},
      } = action as ProductItemInventoriesFetchSucceededAction;
      let workingState = state;

      response.inventories.forEach((res) => {
        const {quantity, inventoryType, _id: itemId} = res;

        if (itemId === state.currentItemMeta.defaultSkuId) {
          workingState = {
            ...workingState,
            currentItemMeta: {
              ...workingState.currentItemMeta,
              quantity,
              inventoryType,
            },
            savedCurrentItemMeta: {
              ...workingState.savedCurrentItemMeta,
              quantity,
              inventoryType,
            },
          };
        }

        const keyPath = [itemId, 'inventory'];
        const value = {quantity, inventoryType} as const;
        workingState = {
          ...workingState,
          currentItemMeta: modifyVariantInItemMeta(
            workingState.currentItemMeta,
            keyPath,
            value
          ),
          savedCurrentItemMeta: modifyVariantInItemMeta(
            workingState.savedCurrentItemMeta,
            keyPath,
            value
          ),
        };
      });

      return merge(state, workingState);
    }

    case PRODUCT_ITEM_META_MODIFIED: {
      const {
        payload: {keyPath, value},
      } = action;

      let adjuster = identity<ItemMetaType>;

      if (keyPath[0] === PRODUCT_QUANTITY_FIELD_META_SLUG) {
        adjuster = (itemMeta: ItemMetaType) => {
          const defaultVariantId = itemMeta.defaultSkuId;
          return merge(itemMeta, {
            variants: itemMeta.variants.map((variant) => {
              if (variant._id === defaultVariantId) {
                return {
                  ...variant,
                  inventory: {
                    inventoryType: INVENTORY_TYPE_FINITE,
                    quantity: value || 0,
                  },
                };
              }
              return variant;
            }),
            quantity: value || 0,
          });
        };
      } else if (keyPath[0] === PRODUCT_INVENTORY_FIELD_META_SLUG) {
        adjuster = (itemMeta: ItemMetaType) => {
          const defaultVariantId = itemMeta.defaultSkuId;
          const savedQuantity = state.savedCurrentItemMeta.quantity || 0;

          return toggleInventoryInItemMeta(
            merge(itemMeta, {
              variants: itemMeta.variants.map((variant) => {
                if (variant._id === defaultVariantId) {
                  const currentInventory =
                    variant.inventory || INFINITE_INVENTORY;
                  if (currentInventory.inventoryType !== value) {
                    return {
                      ...variant,
                      inventory: toggleInventory(
                        currentInventory,
                        savedQuantity
                      ),
                    };
                  }
                }
                return variant;
              }),
            }),
            savedQuantity
          );
        };
      } else if (keyPath[0] === 'variants') {
        adjuster = (itemMeta: ItemMetaType) =>
          merge(itemMeta, {variants: value});
      } else {
        return state;
      }
      const {isInline} = getDependencies();

      return isInline
        ? adjustCurrentInlineItemMeta(adjuster, state)
        : adjustCurrentItemMeta(adjuster, state);
    }

    case DYN_ITEM_SAVED: {
      const {
        payload: {itemSavingFinished},
      } = action;
      if (!itemSavingFinished) {
        return state;
      }
      const {isInline} = getDependencies();

      return isInline
        ? discardCurrentInlineItemMeta(state)
        : discardCurrentItemMeta(state);
    }

    case PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED: {
      const {
        payload: {optionSetId, optionId},
      } = action;
      const adjuster = (itemMeta: ItemMetaType) =>
        merge(itemMeta, {
          variants: assignDefaultOptionToExistingVariants(
            itemMeta.variants,
            optionSetId,
            optionId
          ),
        });
      const {isInline} = getDependencies();

      return isInline
        ? adjustCurrentInlineItemMeta(adjuster, state)
        : adjustCurrentItemMeta(adjuster, state);
    }
    case PRODUCT_OPTION_SET_OPTION_ADDED: {
      const {
        payload: {optionSetId, optionId, variantMintIds},
      } = action;

      const adjuster = (itemMeta: ItemMetaType) =>
        merge(itemMeta, {
          variants: variantsFromNewOption(
            itemMeta.variants,
            optionSetId,
            optionId,
            variantMintIds
          ),
        });
      const {isInline} = getDependencies();

      return isInline
        ? adjustCurrentInlineItemMeta(adjuster, state)
        : adjustCurrentItemMeta(adjuster, state);
    }

    case PRODUCT_OPTION_SET_OPTION_REMOVED: {
      const {
        payload: {optionSetId, optionId, defaultOptionId},
      } = action;
      const {isInline, currentItem} = getDependencies();

      if (!currentItem) {
        return state;
      }

      const optionSets: SkuPropertiesRecordType = currentItem.get(
        'sku-properties',
        List()
      );
      const optionSet = optionSets.find(
        (optionSet_) => optionSet_.get('id') === optionSetId
      );
      if (!optionSet) {
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'OptionSetUnexpectedlyNotFound',
          'Did not unexpectedly find option set',
          {optionSetId}
        );
        return state;
      }
      const options = optionSet.get('enum', List());

      const isDeletingDefaultOption = defaultOptionId === optionId;
      if (isDeletingDefaultOption && options.size > 1) {
        // The default option must be deleted last. We enforce this in the UI.
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'TriedToInvalidlyDeleteDefaultOption',
          'Tried to delete the default option when other options still existed',
          {
            optionId,
            defaultOptionId,
            optionIds: options.map((option) => option.get('id')).toArray(),
          }
        );
      }

      const adjuster = (itemMeta: ItemMetaType) => {
        const newVariants = isDeletingDefaultOption
          ? removeDefaultOptionFromExistingVariants(
              itemMeta.variants,
              optionSetId
            )
          : variantsFromRemovedOption(itemMeta.variants, optionSetId, optionId);
        return merge(itemMeta, {
          variants: newVariants,
        });
      };
      return isInline
        ? adjustCurrentInlineItemMeta(adjuster, state)
        : adjustCurrentItemMeta(adjuster, state);
    }

    case PRODUCT_OPTION_SET_REMOVED: {
      const {
        payload: {optionSetId, defaultOptionId},
      } = action;
      const {isInline, currentItem} = getDependencies();

      if (!currentItem) {
        return state;
      }

      const optionSets: SkuPropertiesRecordType = currentItem.get(
        'sku-properties',
        List()
      );
      const optionSet = optionSets.find(
        (optionSet_) => optionSet_.get('id') === optionSetId
      );
      if (!optionSet) {
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'OptionSetUnexpectedlyNotFound',
          'Did not unexpectedly find option set',
          {optionSetId}
        );
        return state;
      }
      const options = optionSet.get('enum', List());

      if (options.size === 0) {
        // Use has deleted all the options already.
        // No need to do anything.
        return state;
      }

      if (options.every((option) => option.get('id') !== defaultOptionId)) {
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'DefaultOptionIdNotFound',
          'Did not find default option id in options',
          {defaultOptionId}
        );
        return state;
      }

      const nonDefaultOptions = options.filter(
        (option) => option.get('id') !== defaultOptionId
      );

      const adjuster = (itemMeta: ItemMetaType) => {
        const withoutNonDefaultOptions = nonDefaultOptions.reduce(
          (workingVariants, option) =>
            variantsFromRemovedOption(
              workingVariants,
              optionSetId,
              option.get('id')
            ),
          itemMeta.variants
        );
        return merge(itemMeta, {
          variants: removeDefaultOptionFromExistingVariants(
            withoutNonDefaultOptions,
            optionSetId
          ),
        });
      };
      return isInline
        ? adjustCurrentInlineItemMeta(adjuster, state)
        : adjustCurrentItemMeta(adjuster, state);
    }

    case DYN_VARIANT_MODIFIED: {
      const {
        payload: {keyPath, value},
      } = action;
      const VARIANT_QUANTITY_KEYPATH = ['inventory', 'quantity'];

      if (isTrackInventoryBeingToggledOff(keyPath, value)) {
        // reset variant quantity to saved value if `Track inventory` is being toggled off
        const savedVariant = state.savedCurrentItemMeta.variants.find(
          (variant) => variant._id === keyPath[0]
        );
        const savedQuantity =
          savedVariant && savedVariant.inventory
            ? savedVariant.inventory.quantity
            : 0;

        if (!keyPath[0]) {
          // Type check
          return state;
        }

        state = modifyVariantItem(
          state,
          [keyPath[0], ...VARIANT_QUANTITY_KEYPATH],
          savedQuantity
        );
      }

      if (
        // If we are updating the variant trial
        SUBSCRIPTION_TRIAL_PATH.every((elem) => keyPath.includes(elem))
      ) {
        // default value to null if it is not greater than 0
        return modifyVariantItem(
          state,
          keyPath,
          Number(value) === 0 ? null : value
        );
      }

      if (
        // If we are updating the variant quantity
        isEqual(keyPath.slice(keyPath.length - 2), VARIANT_QUANTITY_KEYPATH)
      ) {
        // default value to 0 if it is null
        return modifyVariantItem(state, keyPath, value || 0);
      }

      return modifyVariantItem(state, keyPath, value);
    }

    case DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED: {
      const {
        payload: {keyPath, value},
      } = action;
      return modifyVariantItem(state, keyPath, value);
    }

    case DEFAULT_VARIANT_UPDATED: {
      const {
        payload: {id},
      } = action;
      const currentInlineItemSet = isItemMetaSet(state.currentInlineItemMeta);
      if (id) {
        return currentInlineItemSet
          ? {
              ...state,
              currentInlineItemMeta: {
                ...state.currentInlineItemMeta,
                defaultSkuId: id,
              },
            }
          : {
              ...state,
              currentItemMeta: {
                ...state.currentItemMeta,
                defaultSkuId: id,
              },
            };
      }
      return state;
    }

    case COMMERCE_INCLUDE_DOWNLOADS_TOGGLED: {
      const {
        payload: {value, id},
      } = action;

      if (value) {
        const savedVariant = state.savedCurrentItemMeta.variants.find(
          (variant) => variant._id === id
        );
        return modifyVariantItem(
          state,
          [id, 'download-files'],
          (savedVariant && savedVariant['download-files']) || []
        );
      }

      return modifyVariantItem(state, [id, 'download-files'], []);
    }

    case COMMERCE_CORRUPTED_PRODUCT_DETECTED: {
      // CC-240: an upstream guard caught a corrupted product (orphaned
      // default-sku) before the dispatch that would have crashed the
      // reducer. Reset the item meta so the user can't keep editing on
      // top of stale state and re-persist the orphan on save.
      return {
        ...state,
        currentItemMeta: {...initialState.currentItemMeta},
        savedCurrentItemMeta: {...initialState.savedCurrentItemMeta},
        currentInlineItemMeta: {...initialState.currentInlineItemMeta},
      };
    }

    case COMMERCE_CORRUPTED_VARIANTS_FIXED: {
      const {
        payload: {item},
      } = action;
      const {currentCollection, allVariants} = getDependencies();

      if (currentCollection && isProductsCollection(currentCollection)) {
        const productId = item.get('_id');
        const persistedVariants = allVariants
          .filter((variant) => variant.product === productId)
          .toArray();
        const defaultSkuId = item.get(DEFAULT_SKU_SLUG);
        const allOptionCombinations = optionCombinationsFromOptionSets(
          item.get('sku-properties', List())
        );
        const unusedOptionCombinations = [...allOptionCombinations];
        let invalidPersistedVariants: Array<any> = [];
        const validPersistedVariants: Array<Variant> = [];

        // determine if persistedVariants have valid sku-values
        persistedVariants.forEach((variant) => {
          const matchingOptions = remove(
            unusedOptionCombinations,
            function (optionCombination) {
              // remove option combinations that match a variant's sku-values
              return isEqual(optionCombination, variant['sku-values']);
            }
          );

          // if the variant's sku-values match an option combination
          if (matchingOptions.length) {
            // add the variant to validPersistedVariants
            return validPersistedVariants.push(variant);
          }
          // otherwise we add it to invalidPersistedVariants
          invalidPersistedVariants.push(variant);
        });

        if (
          !unusedOptionCombinations.length &&
          !invalidPersistedVariants.length
        ) {
          return modifyItemVariants(state, validPersistedVariants);
        }

        const hasValidDefaultVariant = validPersistedVariants.find(
          (variant) => variant._id === defaultSkuId
        );

        if (!hasValidDefaultVariant) {
          const defaultVariantIndex = invalidPersistedVariants.findIndex(
            (variant) => variant._id === defaultSkuId
          );

          if (defaultVariantIndex < 0) {
            // CC-240: backstop — the user reached the "fix corrupted
            // variants" path on a product whose default-sku is also
            // missing. The upstream guard in CorruptedVariantsModal
            // dispatches COMMERCE_CORRUPTED_PRODUCT_DETECTED instead of
            // commerceCorruptedVariantsFixed when this is detected.
            // Please see https://github.com/webflow/webflow/pull/39900
            // eslint-disable-next-line webflow/better-bug-reporting
            reportBug('DefaultVariantNotFound', 'no default variant found', {
              metadata: {
                persistedVariants,
                defaultSkuId,
                invalidPersistedVariants,
              },
            });
            return state;
          }

          // if the default variant is not valid we need to make it the
          // first item in the invalidPersistedVariants array so that it
          // will for sure be 'fixed' instead of just removed (i.e. if there
          // are more invalidPersistedVariants than unusedOptionsCombinations)
          const defaultVariant = invalidPersistedVariants.splice(
            defaultVariantIndex,
            1
          );
          invalidPersistedVariants.unshift(defaultVariant[0]);
        }

        invalidPersistedVariants = invalidPersistedVariants.reduce<Array<any>>(
          (invalidVariants, invalidVariant) => {
            const conflictingCombinations = remove(
              unusedOptionCombinations,
              function (optionCombination) {
                const slug = getVariantSlug(optionCombination, item);
                const name = getVariantName(optionCombination, item);
                const hasMatchingCombinations = Object.entries(
                  optionCombination
                ).every(
                  ([key, value]: [any, any]) =>
                    // check if the variant has sku-value pairs that match up with the option combination
                    invalidVariant['sku-values'][key] === value
                );
                return (
                  invalidVariant.name === name ||
                  invalidVariant.slug === slug ||
                  hasMatchingCombinations
                );
              }
            );

            // if the invalid variant has a slug, name or sku-values that match unused option combinations
            if (conflictingCombinations.length) {
              // assign an option combination to the sku-values for the variant and add it to validPersistedVariants
              validPersistedVariants.push({
                ...invalidVariant,
                'sku-values': conflictingCombinations[0],
              });
              return invalidVariants;
            }

            // if the variant hasn't been added to validPersistedVariants, add it to invalidVariants
            return [...invalidVariants, invalidVariant];
          },
          []
        );

        // if there are still unused option combinations
        if (unusedOptionCombinations.length) {
          const variantMintIds = times(unusedOptionCombinations.length, () =>
            makeMintId()
          );
          let mintIdsUsed = 0;

          // assign unused option combinations to sku-values of invalid variants
          unusedOptionCombinations.forEach((optionCombination, index) => {
            let invalidVariant = invalidPersistedVariants[index];
            // if there are no invalid variants to fix
            if (!invalidVariant) {
              const defaultVariant = persistedVariants.find(
                (variant) => variant._id === defaultSkuId
              );
              // create a new variant
              invalidVariant = {
                ...defaultVariant,
                _id: variantMintIds[mintIdsUsed++],
                inventory: INFINITE_INVENTORY,
              };
            }

            // assign the option combination to the sku-values for the variant and add it to validPersistedVariants
            validPersistedVariants.push({
              ...invalidVariant,
              'sku-values': optionCombination,
            });
          });
        }
        return modifyItemVariants(state, validPersistedVariants);
      }
      return state;
    }

    case COMMERCE_PRODUCT_TYPE_CHANGED: {
      const {
        payload: {conflictingFieldSlugs: fields},
      } = action;
      return resetFields(state, fields);
    }

    default: {
      return state;
    }
  }
}

const modifyVariantInItemMeta = (
  state: ItemMetaType,
  keyPath: Array<number | string>,
  value: any
): ItemMetaType => {
  const [id] = keyPath;
  const {variants} = state;
  const variantKey = variants.findKey((variant) => variant._id === id);

  if (typeof variantKey === 'string') {
    const variant = variants.get(variantKey, undefined);
    if (variant) {
      return {
        ...state,
        variants: variants.update(
          variantKey,
          setVariantValue(keyPath.slice(1), value)
        ),
      };
    }
  }

  return state;
};

const modifyVariantItem = (
  state: ProductItemStateType,
  keyPath: Array<number | string>,
  value: any
): ProductItemStateType => {
  const isCurrentInlineItemSet = isItemMetaSet(state.currentInlineItemMeta);

  let inventoryMergeObj: {
    quantity?: any;
    inventoryType?: any;
  } = {} as any;

  const [id, ...keyPathRest] = keyPath;

  if (
    keyPathRest.length === 2 &&
    keyPathRest[0] === 'inventory' &&
    typeof keyPathRest[1] === 'string' &&
    ['inventoryType', 'quantity'].includes(keyPathRest[1])
  ) {
    // Just to make typechecking work
    inventoryMergeObj =
      keyPathRest[1] === 'inventoryType'
        ? {inventoryType: value}
        : {quantity: value};
  }

  return isCurrentInlineItemSet
    ? adjustCurrentInlineItemMeta(
        (itemMeta) => ({
          ...modifyVariantInItemMeta(itemMeta, keyPath, value),
          ...(id === itemMeta.defaultSkuId ? inventoryMergeObj : null),
        }),
        state
      )
    : adjustCurrentItemMeta(
        (itemMeta) => ({
          ...modifyVariantInItemMeta(itemMeta, keyPath, value),
          ...(id === itemMeta.defaultSkuId ? inventoryMergeObj : null),
        }),
        state
      );
};

const discardCurrentInlineItemMeta = (
  state: ProductItemStateType
): ProductItemStateType => ({
  ...state,
  currentInlineItemMeta: {...initialState.currentInlineItemMeta},
});

const discardCurrentItemMeta = (
  state: ProductItemStateType
): ProductItemStateType => {
  return {
    ...state,
    currentItemMeta: {...initialState.currentItemMeta},
    savedCurrentItemMeta: {...initialState.savedCurrentItemMeta},
  };
};

const getItemMeta = (state: ProductItemStateType): ItemMetaType => {
  const {currentItemMeta, currentInlineItemMeta} = state;
  return isItemMetaSet(currentInlineItemMeta)
    ? currentInlineItemMeta
    : currentItemMeta;
};

const toggleInventoryInItemMeta = (
  itemMeta: ItemMetaType,
  savedQuantity: number
): ItemMetaType => ({
  ...itemMeta,
  inventoryType:
    itemMeta.inventoryType === INVENTORY_TYPE_FINITE
      ? INVENTORY_TYPE_INFINITE
      : INVENTORY_TYPE_FINITE,
  quantity:
    itemMeta.inventoryType === INVENTORY_TYPE_FINITE ? 0 : savedQuantity,
});

const toggleInventory = (
  inventory: Inventory,
  savedQuantity: number
): Inventory => {
  if (inventory.inventoryType === INVENTORY_TYPE_FINITE) {
    return {inventoryType: INVENTORY_TYPE_INFINITE, quantity: 0};
  }
  return {inventoryType: INVENTORY_TYPE_FINITE, quantity: savedQuantity};
};

const adjustCurrentItemMeta = (
  adjustFn: (arg1: ItemMetaType) => ItemMetaType,
  state: ProductItemStateType
): ProductItemStateType =>
  merge(state, {
    currentItemMeta: adjustFn(state.currentItemMeta),
  });

const adjustCurrentInlineItemMeta = (
  adjustFn: (arg1: ItemMetaType) => ItemMetaType,
  state: ProductItemStateType
): ProductItemStateType =>
  merge(state, {
    currentInlineItemMeta: adjustFn(state.currentInlineItemMeta),
  });

// reset all measurement fields tied to `shippable` to saved values
const resetMeasurementFields = (
  state: ProductItemStateType
): ProductItemStateType => {
  const {currentItemMeta, savedCurrentItemMeta, currentInlineItemMeta} = state;
  const currentInlineItemSet = isItemMetaSet(state.currentInlineItemMeta);
  const variants = (
    currentInlineItemSet ? currentInlineItemMeta : currentItemMeta
  ).variants;
  const resetVariants = variants.map((variant) => {
    const savedVariant = savedCurrentItemMeta.variants.find(
      (currentSavedVariant) => currentSavedVariant._id === variant._id
    );

    dependentProductFields.shippable.forEach((field) => {
      variant[field] =
        !savedVariant || savedVariant[field] === undefined
          ? null
          : savedVariant[field];
    });

    return variant;
  });

  return modifyVariantItem(state, ['variants'], resetVariants);
};

// reset all conflicting fields for new product type
const resetFields = (
  state: ProductItemStateType,
  fields: Array<string>
): ProductItemStateType => {
  const {currentItemMeta, currentInlineItemMeta} = state;
  const currentInlineItemSet = isItemMetaSet(state.currentInlineItemMeta);
  const variants = (
    currentInlineItemSet ? currentInlineItemMeta : currentItemMeta
  ).variants;
  const resetVariants = variants.map((variant) => {
    fields.forEach((field) => {
      // @ts-expect-error - TS18048 - 'variant' is possibly 'undefined'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Variant'.
      if (variant[field] === undefined) {
        return;
      }
      // @ts-expect-error - TS18048 - 'variant' is possibly 'undefined'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Variant'. | TS18048 - 'variant' is possibly 'undefined'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Variant'.
      variant[field] = resetFieldValue(variant[field]);
    });

    return variant;
  });

  return modifyVariantItem(state, ['variants'], resetVariants);
};

const modifyItemVariants = (
  state: ProductItemStateType,
  variants: Array<Variant>
) => {
  const isInline = isItemMetaSet(state.currentInlineItemMeta);
  const persistedVariantsByOptionCombinationHash =
    generateVariantsByOptionCombinationHash(variants);
  const adjuster = (itemMeta: ItemMetaType) =>
    merge(itemMeta, {
      variants: persistedVariantsByOptionCombinationHash,
    });
  return isInline
    ? adjustCurrentInlineItemMeta(adjuster, state)
    : adjustCurrentItemMeta(adjuster, state);
};
